const keyBy = require('lodash/keyBy')
const get = require('lodash/get')
const uuid = require('uuid/v4')
const { subWeeks } = require('date-fns')

const docToVanRecord = require('../../shipment/tools/doc-to-van-record')
const docToStockCountRecord = require('../..//tools/doc-to-stock-count-record')

const { withChanges } = require('../../common/changes-feed')
const { callInBatches } = require('../../common/call-in-batches')
const { isBasicTierService } = require('../../tools')

const PROGRAM_ID = 'program:shelflife'
const REPORTS_CHECKPOINT_ID = 'stock-situation-reports'
const SHIPMENTS_CHECKPOINT_ID = 'stock-situation-shipments'

const periodIdToYearweek = periodId => periodId.split(/\D+/).join('.')

/* Extract stock events from couchdb and load into postgres
 */
class StockSummaryToAvocado {
  constructor (api, logger, reportsDB, shipmentsDB) {
    this.api = api
    this.logger = logger
    this.reportsDB = reportsDB
    this.shipmentsDB = shipmentsDB
  }

  // checkpoints.reports and checkpoints.shipments are both required objects
  // with read() and write(seq). withChanges rejects a missing one before
  // reading either feed.
  async extractAndQueue (writeToQueue, checkpoints = {}) {
    let items = []
    const changesStats = await withChanges(
      [
        {
          db: this.reportsDB,
          checkpointId: REPORTS_CHECKPOINT_ID,
          checkpoint: checkpoints.reports,
          feedOptions: { batchSize: 100, maxChanges: 400, includeDeleted: true }
        },
        {
          db: this.shipmentsDB,
          checkpointId: SHIPMENTS_CHECKPOINT_ID,
          checkpoint: checkpoints.shipments,
          feedOptions: { batchSize: 100, maxChanges: 400, includeDeleted: true }
        }
      ],
      async ([reportChanges, shipmentChanges]) => {
        const reportDocs = []
        for (const c of reportChanges) {
          if (c.doc && c.doc.type === 'stockCount') {
            reportDocs.push(c.doc)
          } else if (c.deleted && !c.doc) {
            this.logger.warn('stockCount deletion found:', c.id, 'does not have stubs, please clean up avocado database manually')
          }
        }
        const shipmentDocs = []
        for (const c of shipmentChanges) {
          if (c.doc && (c.doc.type === 'snapshot' || c.doc.type === 'change')) {
            shipmentDocs.push(c.doc)
          } else if (c.deleted && !c.doc) {
            this.logger.warn('shipment deletion found:', c.id, 'does not have stubs, please clean up avocado database manually')
          }
        }
        this.logger.info('found report docs', reportDocs.length)
        this.logger.info('found shipment docs', shipmentDocs.length)

        items = await this._changesToQueueItems(reportDocs, shipmentDocs)
        this.logger.info('generated params for', items.length, 'exports')

        if (writeToQueue) {
          await writeToQueue(items)
        }
      }
    )
    this.logger.info(changesStats)
    return items
  }

  async _changesToQueueItems (reportDocs, shipmentDocs) {
    const {
      services, servicesById, locations, locationsById
    } = await this._getRelatedEntities()

    this.logger.info('found', services.length, 'services')
    this.logger.info('found', locations.length, 'locations')

    const reportsForShipments = await this._findReportsForShipmentDocs(locationsById, servicesById, shipmentDocs)
    this.logger.info('found', reportsForShipments.length, 'stock counts for shipments')

    const allReportDocs = reportDocs.concat(reportsForShipments)
    return this._makeExportParamsForReports(services, servicesById, allReportDocs)
  }

  async transformAndLoadEvents (exportParams) {
    const transactionId = uuid()
    const locationIds = [...exportParams.reduce(
      (set, e) => set.add(e.locationId), new Set()
    )]

    const locations = await this.api.location.getByIds(locationIds)
    const locationsById = keyBy(locations, '_id')

    this.logger.info('exports', exportParams.map(p => p.locationId + ' - ' + p.periodId))

    // run the export for a location/period and post rows via the stock pg adapter
    const transformAndLoadLocation = async ({ locationId, serviceId, periodId }) => {
      // find the location
      const location = locationsById[locationId]
      if (!location) {
        // If we don't find the location, we just skip
        this.logger.warn('Location not found', locationId, 'skipping export for period', periodId)
        return
      }
      // Occasionally we run into locations that don't have a uuid,
      // which means they cannot be linked to a location in postgres
      // and would make posting the export result to avocado fail.
      if (!location.additionalData.uuid) {
        this.logger.warn('Location is missing uuid', locationId, 'skipping export for period', periodId)
        return
      }

      const locationClassification = get(location, 'additionalData.classification')

      // This is like held or central location
      if (locationClassification === 'Pack Point') {
        console.log('Temporarily skip pack points')
        return
      }

      // run the stock situation export
      let exportData
      try {
        this.logger.info('starting stock situation export for:', locationId, periodId, serviceId)
        exportData = await this.api.stock.stockSituationEvent(
          locationId, serviceId, periodId, {
            locations,
            includeChildren: false,
            locale: 'en-US',
            adjustments: true
          }
        )
        this.logger.info('finished stock situation export for:', locationId, periodId)
      } catch (err) {
        this.logger.warn('failed to export stock situation report for:', locationId, periodId)
        throw err
      }

      // transform stock situation export data to rows for the stock pg adapter
      // Stock Situation 2 returns objects
      const locationUuid = get(location, 'additionalData.uuid')
      // TODO: rename location_id to location_fsid

      if (!exportData) {
        this.logger.warn('No stock situation events to export for:', locationId, periodId, serviceId)
        return
      }

      const payloadRows = exportData.map(row => {
        return {
          ...row,
          transaction_id: transactionId,
          location_id: locationUuid,
          service_id: serviceId,
          data_source: 'event'
        }
      })

      // post rows to stock pg adapter
      const yearweek = periodIdToYearweek(periodId)
      this.logger.info('inserting rows into RDS', locationUuid, yearweek, payloadRows.length)
      try {
        await this.api.stock.pgAdapter.createStockSummaryEvents(locationUuid, yearweek, payloadRows)
      } catch (err) {
        this.logger.info('failed to insert stock situation rows into RDS for', locationId, periodId, err)
        throw err
      }
    }

    // Run the export and post the result to avocado in batches
    const promiseCreators = exportParams.map(
      (params) => () => transformAndLoadLocation(params)
    )
    return callInBatches(promiseCreators, { batchSize: 5 })
  }

  async resetCheckpoints (checkpoints) {
    if (!checkpoints || !checkpoints.reports || !checkpoints.shipments) {
      throw new Error('reports and shipments checkpoints are required')
    }
    const reportsCheckpoint = checkpoints.reports
    const shipmentsCheckpoint = checkpoints.shipments
    try {
      await reportsCheckpoint.reset()
      await shipmentsCheckpoint.reset()
    } catch (err) {
      this.logger.warn('failed to reset checkpoints')
      throw err
    }
  }

  async _getRelatedEntities (locationIds = null) {
    // TODO: use cache if possible
    const services = await this.api.service.getAll(PROGRAM_ID)
    const servicesById = keyBy(services, 'id')
    const locations = await (
      locationIds != null ? this.api.location.getByIds(locationIds) : this.api.location.listAll()
    )
    const locationsById = keyBy(locations, '_id')
    return {
      services,
      servicesById,
      locations,
      locationsById
    }
  }

  _makeExportParams (services, locationId, periodId) {
    for (const service of services) {
      for (const serviceLocationId of service.locations) {
        if (locationId.startsWith(serviceLocationId)) {
          return {
            key: locationId + ':' + periodId,
            value: {
              locationId,
              serviceId: service.id,
              periodId
            }
          }
        }
      }
    }
  }

  async _findReportsForShipmentDocs (locationsById, servicesById, docs) {
    // Build a list of `{ endDate, locationId }` tuples for findReports.
    // We want to find reports for received snapshots and shipment adjustments.
    const params = []
    for (const doc of docs) {
      let endDates
      let record
      try {
        record = docToVanRecord(doc)
      } catch (e) {
        this.logger.warn('Error creating snapshot', doc._id)
        continue
      }
      if (doc.type === 'change' && doc._id.includes('adjustment')) {
        endDates = [record.createdAt, record.effectiveAt]
      } else if ((doc.type === 'snapshot' && doc._id.includes('received'))) {
        endDates = [record.createdAt]
      } else {
        continue
      }
      for (const endDate of endDates) {
        const startDate = subWeeks(endDate, 4).toJSON()
        const origin = locationsById[record.origin.id]
        if (!origin) {
          this.logger.info('Could not find origin location of', record.id)
        }
        const destination = locationsById[record.destination.id]
        if (!destination) {
          this.logger.info('Could not find destination location of', record.id)
        }
        for (const location of [origin, destination]) {
          if (location == null) {
            continue
          }
          const serviceId = get(location, 'programs.0.services.0.id')
          const service = servicesById[serviceId]
          if (!service) {
            this.logger.info('Error could not find service', serviceId, 'of', record.id)
            continue
          }
          params.push({
            location,
            startDate,
            endDate,
            service
          })
        }
      }
    }

    const scaledDownParams = Object.values(params.reduce((acc, val) => {
      const key = `${val.location._id}-${val.service.id}`
      const current = acc[key]

      if (!current) {
        acc[key] = {
          location: val.location,
          startDate: val.startDate,
          endDate: val.endDate,
          service: val.service
        }
      } else {
        current.startDate = val.startDate < current.startDate
          ? val.startDate
          : current.startDate

        if (val.endDate > current.endDate) {
          current.endDate = val.endDate
        }
      }

      return acc
    }, {}))

    const reports = []
    for (const { startDate, endDate, location, service } of scaledDownParams) {
      const reportRows = await this.api.report.findForLocation({
        location,
        service,
        startDate,
        endDate,
        entityOptions: { rawRows: true }
      })
      const rows = reportRows.map(r => r.doc).filter(d => !!d)
      this.logger.info('found', rows.length, 'reports for', location._id, 'between', startDate, 'and', endDate)
      if (rows.length === 0) {
        continue
      }

      // take the most recent report
      let report = rows[0]
      for (const r of rows) {
        if (r.submittedAt > report.submittedAt) {
          report = r
        }
      }
      reports.push(report)
    }

    return reports
  }

  _makeExportParamsForReports (services, servicesById, docs) {
    const exportParamsMap = new Map()

    for (const doc of docs) {
      const service = servicesById[doc.serviceId]
      if (!service) {
        this.logger.warn(`no service found for doc ${doc._id}`)
        continue
      }
      let stockCountRecord
      try {
        stockCountRecord = docToStockCountRecord(doc, service)
      } catch (error) {
        // lots of docs on dev data come here
        this.logger.warn(`could not docToStockCountRecord for doc ${doc._id}`, error)
        continue
      }
      const {
        date: { reportingPeriod },
        location: { id: locationId }
      } = stockCountRecord
      let params
      if (isBasicTierService(service.id)) {
        params = {
          key: locationId + ':' + reportingPeriod,
          value: {
            locationId,
            serviceId: service.id,
            periodId: reportingPeriod
          }
        }
      } else {
        params = this._makeExportParams(services, locationId, reportingPeriod)
      }
      if (!params) {
        this.logger.warn('Failed to make export params for stock count', doc.id)
        continue
      }
      exportParamsMap.set(params.key, params.value)
    }
    return [...exportParamsMap.values()]
  }
}

module.exports = StockSummaryToAvocado
// Need to manipulate those in testing
module.exports.REPORTS_CHECKPOINT_ID = REPORTS_CHECKPOINT_ID
module.exports.SHIPMENTS_CHECKPOINT_ID = SHIPMENTS_CHECKPOINT_ID
